import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabase';
import { normalizeGrade } from '../../utils/rosterGrouping';

/**
 * After-school children whose grade cannot be read, and who are therefore in no Chinese or
 * Math class.
 *
 * This is the one silent failure the virtual-class design has. Membership is
 * `normalizeGrade(student) === class.grade`, so a child whose grade is blank, misspelled,
 * or something like "Pre-K" simply never appears on a roster — and nothing looks wrong on
 * screen, because an absent name looks exactly like a child who is not in that grade. A
 * roster that quietly omits a child is the failure worth surfacing loudly.
 *
 * Renders nothing when every grade maps, which is the normal case: production currently has
 * none. It is here so that the day someone types "Grade Three" into a student record, an
 * admin finds out from this banner rather than from a teacher wondering where a child went.
 */

interface EnrollmentRow {
  student: {
    id: string;
    first_name: string;
    last_name: string;
    metadata: { grade?: string } | null;
  } | null;
}

interface UnmappedStudent {
  id: string;
  name: string;
  /** Exactly what is on the record, so an admin can see what needs correcting. */
  rawGrade: string;
}

export function useUnmappedGradeStudents() {
  return useQuery<UnmappedStudent[]>({
    queryKey: ['unmapped-grade-students'],
    queryFn: async () => {
      // `!inner` on both embeds makes them filtering joins, so the scoping happens in the
      // query rather than over a full table read.
      const { data, error } = await supabase
        .from('enrollments')
        .select('student:students!inner(id, first_name, last_name, metadata), programs!inner(type, active)')
        .eq('is_active', true)
        .eq('programs.type', 'after-school')
        .eq('programs.active', true);
      if (error) throw error;

      const seen = new Map<string, UnmappedStudent>();
      for (const row of (data ?? []) as unknown as EnrollmentRow[]) {
        const s = row.student;
        if (!s || seen.has(s.id)) continue;
        const raw = s.metadata?.grade ?? '';
        // The same function the database uses to decide membership, so this banner cannot
        // disagree with the rosters it is warning about.
        if (normalizeGrade(raw) !== null) continue;
        seen.set(s.id, {
          id: s.id,
          name: `${s.first_name} ${s.last_name}`,
          rawGrade: raw.trim() === '' ? '(blank)' : raw,
        });
      }
      return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}

export function UnmappedGradeWarning() {
  const { data: students = [] } = useUnmappedGradeStudents();
  if (students.length === 0) return null;

  return (
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600 mt-0.5" aria-hidden="true" />
        <div className="text-sm text-amber-900">
          <p className="font-medium">
            {students.length === 1
              ? '1 after-school student is in no Chinese or Math class'
              : `${students.length} after-school students are in no Chinese or Math class`}
          </p>
          <p className="mt-0.5 text-amber-800">
            Their grade cannot be read, so they will not appear on any virtual class roster.
            Fix the grade on the student record and they appear automatically.
          </p>
          <ul className="mt-2 space-y-0.5">
            {students.map(s => (
              <li key={s.id}>
                <Link to={`/admin-app/students?student=${s.id}`} className="underline hover:no-underline">
                  {s.name}
                </Link>
                <span className="text-amber-700"> — grade reads “{s.rawGrade}”</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
